import {
  Cart,
  CartItem,
  CartUser,
  InsuranceProduct,
  LabelProduct,
  Leg,
  Traveler,
} from '@/types/cart'
import {
  OrderSummaryAddress,
  OrderSummaryLeg,
  OrderSummaryLegTraveler,
  OrderSummaryOrder,
  OrderSummaryProductFee,
  OrderSummaryProductInsurance,
  OrderSummaryProductLabel,
  OrderSummaryProductProductLine,
  OrderSummaryShipment,
  OrderSummaryUser,
} from '@/types/order_summary'

export const resolveInternational = (cart?: Cart | null): boolean => {
  if (!cart || !cart.legs?.[0]) return false
  const domestic = ['US:US', 'CA:CA']
  const leg = cart.legs[0]
  const origin_country = leg.shipRoute?.origin?.countryCode?.toUpperCase()
  const destination_country = leg.shipRoute?.destination?.countryCode?.toUpperCase()
  if (!origin_country || !destination_country) return false
  const key = `${origin_country}:${destination_country}`
  return domestic.indexOf(key) == -1
}

/**
 * Converts Cart data to OrderSummaryOrder format
 * This is the reverse of reformatOrderSummary function
 */
export const generateOrderSummaryFromCart = (cart: Cart): OrderSummaryOrder => {
  return {
    id: cart.id,
    totalPriceCents: cart.totalAmountCents,
    orderId: cart.orderId || '',
    state: 'pending',
    receiptViews: 0,
    device: cart.device,
    userId: cart.user.id,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    user: mapUserToOrderSummaryUser(cart.user),
    legs:
      cart.legs?.map((leg, index) => mapLegToOrderSummaryLeg(leg, index, cart.items || [])) || [],
    transactions: [],
  }
}

const mapLegToOrderSummaryLeg = (
  leg: Leg,
  legIndex: number,
  cartItems: CartItem[],
): OrderSummaryLeg => {
  const legItems = cartItems.filter((item) => item.leg?.id === leg.id)
  const travelerGroups = groupCartItemsByTraveler(legItems)

  return {
    id: leg.id || `leg_${legIndex}`,
    legId: leg.id || `leg_${legIndex}`,
    legIndex: leg.legIndex,
    pickup: leg.pickup,
    legName: leg.legIndex === 0 ? 'Outbound' : 'Return',
    origin: mapShipPointToOrderSummaryAddress(
      leg.shipRoute?.origin as OrderAddress,
      `origin_${legIndex}`,
    ),
    destination: mapShipPointToOrderSummaryAddress(
      leg.shipRoute?.destination as OrderAddress,
      `destination_${legIndex}`,
    ),
    pickupFeeCents: calculatePickupFees(leg),
    travelers: Object.entries(travelerGroups).map(([, items]) =>
      mapTravelerToOrderSummaryLegTraveler(items[0].traveler, items, leg),
    ),
  }
}

const mapShipPointToOrderSummaryAddress = (
  shipPoint: OrderAddress,
  fallbackId: string,
): OrderSummaryAddress => {
  if (!shipPoint) {
    return {
      id: fallbackId,
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip5: '',
      country: 'US',
      countryCode: 'US',
      phoneNumber: '',
    }
  }

  return {
    id: fallbackId,
    address1: shipPoint?.address_1 || '',
    address2: shipPoint?.address_2 || '',
    city: shipPoint?.city || '',
    state: shipPoint?.state || '',
    zip5: shipPoint?.zip || '',
    country: shipPoint?.country_code || 'US',
    countryCode: shipPoint?.country_code || 'US',
    phoneNumber: shipPoint?.phone_number || '',
  }
}

const groupCartItemsByTraveler = (cartItems: CartItem[]): Record<string, CartItem[]> => {
  return cartItems.reduce(
    (groups, item) => {
      const travelerId = item.traveler.id || item.traveler.email || 'default'
      if (!groups[travelerId]) {
        groups[travelerId] = []
      }
      groups[travelerId].push(item)
      return groups
    },
    {} as Record<string, CartItem[]>,
  )
}

const mapTravelerToOrderSummaryLegTraveler = (
  traveler: Traveler,
  cartItems: CartItem[],
  leg: Leg,
): OrderSummaryLegTraveler => {
  return {
    id: traveler.id || `traveler_${traveler.email}`,
    name: traveler.name,
    email: traveler.email,
    phone: traveler.phone,
    sms: false,
    shipments: cartItems.map((item) => mapCartItemToOrderSummaryShipment(item, leg)),
  }
}

const mapCartItemToOrderSummaryShipment = (cartItem: CartItem, leg: Leg): OrderSummaryShipment => {
  const products = cartItem.products.map((cartItemProduct) => {
    const productType = cartItemProduct.targetProductType

    if (productType === 'insurance') {
      return {
        type: 'insurance' as const,
        id: cartItemProduct.product.id,
        coverageAmountCents: (cartItemProduct.product as InsuranceProduct).coverageAmountCents || 0,
        productLine: mapProductLineToOrderSummary(
          (cartItemProduct.product as LabelProduct).productLine,
        ),
        priceCents: cartItemProduct.priceCents,
      } as OrderSummaryProductInsurance
    }

    if (productType === 'fee') {
      return {
        type: 'fee' as const,
      } as OrderSummaryProductFee
    }

    return {
      type: 'label' as const,
      id: cartItemProduct.product.id,
      displayName:
        (cartItemProduct.product as LabelProduct).displayName || cartItemProduct.product.name,
      fullName: cartItemProduct.product.name,
      name: cartItemProduct.product.name,
      weight: (cartItemProduct.product as LabelProduct).weight || 0,
      productLine: mapProductLineToOrderSummary(
        (cartItemProduct.product as LabelProduct).productLine,
      ),
      priceCents: cartItemProduct.priceCents,
      packagingType: cartItem.packaging?.systemName,
      coverageAmountCents: (cartItemProduct.product as InsuranceProduct).coverageAmountCents,
    } as OrderSummaryProductLabel
  })

  return {
    id: cartItem.id,
    carrierTrackingId: cartItem.trackingId || '',
    trackingId: cartItem.trackingId || '',
    shipDate: cartItem.shipDate || '',
    arrivalDate: cartItem.desiredArrivalDate || '',
    estimatedCarrierDeliveryDate: cartItem.desiredArrivalDate || '',
    updatedAt: new Date().toISOString(),
    humanizedCarrierServiceType: 'Standard Service',
    products,
    carrierServiceLevel: {
      id: cartItem.products[0]?.serviceRate?.carrierServiceLevel?.displayName || 'standard',
      displayName: 'Standard Service',
      carrier: {
        code: 'FDXE',
        systemName: 'FEDEX',
        displayName: 'FedEx',
      },
    },
    discountCents: 0,
    wholesaleDiscountCents: 0,
    upgradeDiscount: 0,
    retailShipRate: calculateRetailShipRate(cartItem),
    paidInsuranceCents: calculateInsuranceCost(cartItem),
    handlingFeeCents: calculateHandlingFees(cartItem),
    fuelSurchargeCents: calculateFuelSurcharge(),
    clearanceFeeCents: calculateClearanceFee(cartItem),
    totalPriceCents: calculateTotalPrice(cartItem),
    shipmentLegName: leg.legIndex === 0 ? 'Outbound' : 'Return',
    packagingId: cartItem.packaging?.id,
    additionalInfo: cartItem.additionalInfo,
  }
}

const mapProductLineToOrderSummary = (productLine?: string): OrderSummaryProductProductLine => {
  if (!productLine) {
    return {
      id: 'general',
      displayName: 'General',
    }
  }

  return {
    id: productLine,
    displayName: productLine.charAt(0).toUpperCase() + productLine.slice(1),
  }
}

const mapUserToOrderSummaryUser = (user: CartUser): OrderSummaryUser => {
  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phoneNumber: user.phoneNumber,
    countryCode: user.countryCode || 'US',
    hasTempPass: false,
    vip: false,
    mobileVerificationEligible: false,
    mobileVerified: false,
    lifetimeShipments: 0,
    termsAccepted: false,
  }
}

// Calculation helper functions
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calculatePickupFees = (leg: Leg): number => {
  return 0
}

const calculateRetailShipRate = (cartItem: CartItem): number => {
  return cartItem.products.reduce((total, product) => {
    return total + (product.serviceRate?.priceCents || 0)
  }, 0)
}

const calculateInsuranceCost = (cartItem: CartItem): number => {
  const insuranceProduct = cartItem.products.find((p) => p.targetProductType === 'insurance')
  return insuranceProduct?.priceCents || 0
}

const calculateHandlingFees = (cartItem: CartItem): number => {
  const feeProducts = cartItem.products.filter((p) => p.targetProductType === 'fee')
  return feeProducts.reduce((total, product) => total + product.priceCents, 0)
}

const calculateFuelSurcharge = (): number => {
  return 0
}

const calculateClearanceFee = (cartItem: CartItem): number => {
  return cartItem.products
    .filter((p) => p.targetProductType === 'clearance')
    .reduce((total, product) => total + product.priceCents, 0)
}

const calculateTotalPrice = (cartItem: CartItem): number => {
  return cartItem.products.reduce((total, product) => {
    return total + product.priceCents + product.taxCents
  }, 0)
}
